import makeApi, { URLS } from "Utils/apiURL";

// Vocabularies for the "Find prospects" List Builder prefill.
//
// INDUSTRIES — the source of truth is the LIVE List Builder vocabulary
// (GET /ss/list-builder/industries — the same endpoint the industry
// autocomplete uses). Derived ICP industries are validated against it AT
// CLICK TIME with normalized matching, so vocabulary drift can never
// silently break the mapping again ("Information Technology" existing only
// in the live list, "&" vs "and" variants, etc.). The static list below is
// only the offline FALLBACK if that API call fails.

export const FALLBACK_INDUSTRIES = [
  "Accounting", "Airlines/Aviation", "Alternative Dispute Resolution",
  "Alternative Medicine", "Animation", "Apparel & Fashion",
  "Architecture & Planning", "Arts and Crafts", "Automotive",
  "Aviation & Aerospace", "Banking", "Biotechnology",
  "Broadcast Media", "Building Materials", "Business Supplies and Equipment",
  "Capital markets", "Chemicals", "Civic & Social Organization",
  "Civil Engineering", "Commercial Real Estate", "Computer & Network Security",
  "Computer Games", "Computer Hardware", "Computer Networking",
  "Computer Software", "Construction", "Consumer Electronics",
  "Consumer Goods", "Cosmetics", "Dairy",
  "Defense & Space", "Design", "E-Learning",
  "Education Management", "Electrical/Electronic Manufacturing", "Entertainment",
  "Environmental Services", "Events Services", "Executive Office",
  "Facilities Services", "Farming", "Financial Services",
  "Fine Art", "Fishery", "Food & Beverages",
  "Food Production", "Fund-Raising", "Furniture",
  "Gambling & Casinos", "Glass, Ceramics & Concrete", "Government Administration",
  "Government Relations", "Graphic Design", "Health, Wellness and Fitness",
  "Higher Education", "Hospital & Health Care", "Hospitality",
  "Human Resources", "Import and Export", "Individual & Family Services",
  "Industrial Automation", "Information Services", "Information Technology and Services",
  "Insurance", "International Trade and Development", "Internet",
  "Investment Banking", "Investment Management", "Judiciary",
  "Law Enforcement", "Law Practice", "Legal Services",
  "Legislative Office", "Leisure, Travel & Tourism", "Local Business",
  "Logistics and Supply Chain", "Luxury Goods & Jewelry", "Machinery",
  "Management Consulting", "Maritime", "Market Research",
  "Marketing and Advertising", "Mechanical or Industrial Engineering", "Media Production",
  "Medical Device", "Medical Devices", "Medical Practice",
  "Mental Health Care", "Military", "Mining & Metals",
  "Motion Pictures and Film", "Museums and Institutions", "Music",
  "Nanotechnology", "Newspapers", "Oil & Energy",
  "Online Media", "Outsourcing/Offshoring", "Package/Freight Delivery",
  "Packaging and Containers", "Paper & Forest Products", "Performing Arts",
  "Pharmaceuticals", "Philanthropy", "Photography",
  "Plastics", "Primary/Secondary Education", "Printing",
  "Professional Training & Coaching", "Program Development", "Public Relations and Communications",
  "Public Safety", "Publishing", "Railroad Manufacture",
  "Ranching", "Real Estate", "Recreational Facilities and Services",
  "Religious Institutions", "Renewables & Environment", "Research",
  "Restaurants", "Security and Investigations", "Semiconductors",
  "Shipbuilding", "Sporting Goods", "Sports",
  "Staffing and Recruiting", "Supermarkets", "Textiles",
  "Think Tanks", "Tobacco", "Translation and Localization",
  "Transportation/Trucking/Railroad", "Travel, Recreation, and Leisure", "Utilities",
  "Venture Capital & Private Equity", "Veterinary", "Warehousing",
  "Wholesale & Distribution", "Wine and Spirits", "Wireless",
  "Writing and Editing"
];

// "&"/"and", punctuation and spacing variants all normalize to the same key.
const normalizeIndustry = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// LLM output still speaks the OLD LinkedIn taxonomy; the live vocabulary
// carries the 2022 one. Neither exact nor containment matching bridges
// "Computer Software" -> "Software Development", so try these renames too.
// Keyed and valued in normalized form; only the ones the search would
// otherwise silently drop, not the full taxonomy.
const INDUSTRY_ALIASES = {
  "computer software": ["software development"],
  "information technology and services": ["it services and it consulting"],
  "information technology": ["it services and it consulting"],
  "internet": ["technology information and internet"],
  "marketing and advertising": ["advertising services"],
  "management consulting": ["business consulting and services"],
  "hospital and health care": ["hospitals and health care"],
  "human resources": ["human resources services"],
  "e learning": ["e learning providers"],
  "online media": ["online audio and video media"],
  "computer hardware": ["computer hardware manufacturing"],
  "computer networking": ["computer networking products"],
  "consumer electronics": ["computers and electronics manufacturing"],
  "medical device": ["medical equipment manufacturing"],
  "medical devices": ["medical equipment manufacturing"],
  "pharmaceuticals": ["pharmaceutical manufacturing"],
  "biotechnology": ["biotechnology research"],
  "logistics and supply chain": ["transportation logistics supply chain and storage"],
  "automotive": ["motor vehicle manufacturing"],
  "oil and energy": ["oil and gas"],
  "renewables and environment": ["renewable energy semiconductor manufacturing"],
  "semiconductors": ["semiconductor manufacturing"],
  "machinery": ["machinery manufacturing"],
  "chemicals": ["chemical manufacturing"],
  "wireless": ["wireless services"],
  "public relations and communications": ["public relations and communications services"],
  "venture capital and private equity": ["venture capital and private equity principals"],
  "nonprofit organization management": ["non profit organizations"],
  "education management": ["education administration programs"],
  "outsourcing offshoring": ["outsourcing and offshoring consulting"],
  "research": ["research services"],
  "design": ["design services"],
  "entertainment": ["entertainment providers"],
  "food and beverages": ["food and beverage services"],
  "food production": ["food and beverage manufacturing"],
  "apparel and fashion": ["retail apparel and fashion"],
  "wholesale and distribution": ["wholesale"],
  "warehousing": ["warehousing and storage"],
  "medical practice": ["medical practices"],
  "health wellness and fitness": ["wellness and fitness services"],
  "leisure travel and tourism": ["travel arrangements"],
  "printing": ["printing services"],
  "furniture": ["furniture and home furnishings manufacturing"],
  "industrial automation": ["automation machinery manufacturing"],
  "mechanical or industrial engineering": ["industrial machinery manufacturing"],
  "electrical electronic manufacturing": ["appliances electrical and electronics manufacturing"],
  "defense and space": ["defense and space manufacturing"],
  "aviation and aerospace": ["aviation and aerospace component manufacturing"],
  "airlines aviation": ["airlines and aviation"],
  "mining and metals": ["mining"],
  "broadcast media": ["broadcast media production and distribution"],
  "publishing": ["book and periodical publishing"],
  "civic and social organization": ["civic and social organizations"],
  "government relations": ["government relations services"],
  "veterinary": ["veterinary services"]
};

let liveIndustriesCache = null;

// Fetch (once per session) the live industry vocabulary the List Builder uses.
export const warmIndustriesVocab = async () => {
  if (liveIndustriesCache) return liveIndustriesCache;
  try {
    const res = await makeApi({ url: URLS.industriesListV2, method: "GET" });
    const list = res?.data?.industries;
    if (Array.isArray(list) && list.length) {
      liveIndustriesCache = list;
      return liveIndustriesCache;
    }
  } catch (e) {
    /* fall through to static fallback */
  }
  return FALLBACK_INDUSTRIES;
};

// Map derived industry values onto the live vocabulary:
//   1. exact normalized match ("Information technology & services" ≡
//      "Information Technology and Services")
//   2. unique-containment fallback — only when exactly ONE live entry
//      contains the value (deterministic; never fans out to wrong matches)
//   3. otherwise DROP (and log) rather than sending a filter the search
//      would silently mismatch.
export const mapIndustriesToListBuilder = async (values) => {
  const wanted = (values || []).filter(Boolean);
  if (!wanted.length) return [];
  const vocab = await warmIndustriesVocab();
  const byNorm = {};
  vocab.forEach((name) => { byNorm[normalizeIndustry(name)] = name; });

  const out = [];
  const dropped = [];
  wanted.forEach((v) => {
    const n = normalizeIndustry(v);
    // The value itself first, then its taxonomy renames: exact match wins,
    // unique containment is the fallback for each candidate in turn.
    const candidates = [n, ...(INDUSTRY_ALIASES[n] || [])];
    const exact = candidates.find((c) => byNorm[c]);
    if (exact) {
      out.push(byNorm[exact]);
      return;
    }
    let hit = null;
    candidates.some((c) => {
      const containing = vocab.filter((name) => {
        const nn = normalizeIndustry(name);
        return nn.includes(c) || c.includes(nn);
      });
      if (containing.length === 1) hit = containing[0];
      return Boolean(hit);
    });
    if (hit) out.push(hit);
    else dropped.push(v);
  });
  if (dropped.length) {
    // Surfaced for debuggability — a dropped value means the intelligence
    // emitted something the List Builder genuinely doesn't have.
    console.warn("[FindProspects] industries not in List Builder vocab, dropped:", dropped);
  }
  return [...new Set(out)];
};

export const EMPLOYEE_RANGE_IDS = [
  "1 - 10", "11 - 20", "21 - 50", "51 - 100", "101 - 200", "201 - 500",
  "501 - 1000", "1001 - 2000", "2001 - 5000", "5001 - 10000", "10000+"
];

export const REVENUE_RANGE_IDS = [
  "0-99999", "100000-499999", "500000-999999", "1000000-4999999",
  "5000000-9999999", "10000000-24999999", "25000000-49999999",
  "50000000-99999999", "100000000-499999999", "500000000-999999999",
  "1000000000-4999999999", "5000000000-9999999999", "10000000000+"
];
